import classNames from 'classnames'
import { FC, useState } from 'react'
import { AiToolIcon } from 'src/components/AiToolIcon'
import { DeepDiveEngine } from 'src/services/aiListeningApi/brandProfile.types'
import styles from './deepDive.module.scss'
import {
  AIResponseModal,
  AiResponseWithCitations,
} from '../shared/AIResponseModal'
import { Pill, ROLE_TONES } from '../shared/Pill'
import { ROLE_LABELS } from '../shared/utils'

// The preview is the answer flattened to plain text, so markdown leftovers
// remain: table rules ("---:|---"), horizontal rules and "[1][3]" citation
// markers. They are noise in a two-line teaser.
const cleanPreview = (text: string): string =>
  text
    .replace(/(?:\[\d+\])+/g, '')
    .replace(/\|?\s*:?-{3,}:?(?:\s*\|?\s*:?-{3,}:?)*\s*\|?/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim()

// The shared response modal reads the legacy response document.
const toModalResponse = (engine: DeepDiveEngine): AiResponseWithCitations => ({
  aiTool: engine.aiTool,
  brandMentioned: engine.brandMentioned,
  citations: engine.citations,
  competitors: engine.ranking
    .filter((item) => !item.isTarget)
    .map((item) => item.name),
  domainMentioned: engine.domainCited,
  fullResponse: engine.fullResponse,
  model: engine.model,
})

interface EngineCardProps {
  engine: DeepDiveEngine
  onShowResponse: () => void
}

// The whole card opens the full response, like its "Show Full Response" link.
const EngineCard: FC<EngineCardProps> = ({ engine, onShowResponse }) => (
  <article
    className={classNames(styles.engine, styles.engineClickable)}
    onClick={onShowResponse}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        onShowResponse()
      }
    }}
    role="button"
    tabIndex={0}
  >
    <div className={styles.engineHeader}>
      <span className={styles.engineName}>
        <AiToolIcon className={styles.engineIcon} name={engine.aiTool} />
        {engine.aiTool}
        {engine.model && ` (${engine.model})`}
      </span>
      <Pill outline tone={ROLE_TONES[engine.status]} uppercase>
        {ROLE_LABELS[engine.status]}
      </Pill>
    </div>

    <div className={styles.engineFacts}>
      <div className={styles.engineFactsRow}>
        <span>
          Brand Status:{' '}
          <strong className={engine.brandMentioned ? styles.good : styles.bad}>
            {engine.brandMentioned ? 'Mentioned' : 'Not mentioned'}
          </strong>
        </span>
        <span>
          Avg.Position:{' '}
          <strong>{engine.position ? `#${engine.position}` : '—'}</strong>
        </span>
        <span>
          Domain Cited:{' '}
          <strong className={engine.domainCited ? styles.good : styles.bad}>
            {engine.domainCited ? 'Yes' : 'No'}
          </strong>
        </span>
      </div>
      <div
        className={classNames(styles.engineFactsRow, styles.engineFactsStart)}
      >
        <span>
          Competitor: <strong>{engine.mainCompetitor?.name ?? '—'}</strong>
        </span>
        {engine.mainCompetitor?.association && (
          <span>
            Association:{' '}
            <strong className={styles.accent}>
              {engine.mainCompetitor.association}
            </strong>
          </span>
        )}
      </div>
    </div>

    <div className={styles.engineSection}>
      <span className={styles.engineSectionTitle}>Ranked recommendation</span>
      {engine.ranking.length === 0 ? (
        <span className={styles.muted}>No brands recommended</span>
      ) : (
        <ol className={styles.ranking}>
          {engine.ranking.map((item) => (
            <li
              className={classNames(styles.rankingItem, {
                [styles.rankingTarget]: item.isTarget,
              })}
              key={`${item.rank}-${item.name}`}
            >
              <span className={styles.rankingName}>
                {item.rank}. {item.name}
                {item.isTarget && ' (You)'}
              </span>
              <Pill outline tone={ROLE_TONES[item.role]} uppercase>
                {ROLE_LABELS[item.role]}
              </Pill>
            </li>
          ))}
        </ol>
      )}
    </div>

    <div className={styles.engineSection}>
      <span className={styles.engineSectionHead}>
        <span className={styles.engineSectionTitle}>Full response preview</span>
        <button
          className={styles.linkButton}
          onClick={(event) => {
            event.stopPropagation()
            onShowResponse()
          }}
          type="button"
        >
          Show Full Response
        </button>
      </span>
      <p className={styles.preview}>{cleanPreview(engine.responsePreview)}</p>
    </div>
  </article>
)

// One card per monitored AI engine, two to a row.
export const EngineCards: FC<{ engines: DeepDiveEngine[] }> = ({ engines }) => {
  const [openEngine, setOpenEngine] = useState<DeepDiveEngine | null>(null)

  if (engines.length === 0) {
    return <p className={styles.muted}>No AI engine answered in this run.</p>
  }

  return (
    <>
      <div className={styles.engines}>
        {engines.map((engine) => (
          <EngineCard
            engine={engine}
            key={engine.aiTool}
            onShowResponse={() => setOpenEngine(engine)}
          />
        ))}
      </div>
      {openEngine && (
        <AIResponseModal
          modelLabel={openEngine.model}
          onClose={() => setOpenEngine(null)}
          response={toModalResponse(openEngine)}
        />
      )}
    </>
  )
}
